import { useContext, createContext } from 'react';

export type InboxPreviewState = {
  qeId: string;
  selectedAddDiscount: boolean;
  successfulPayment: boolean;
  isTrialer: boolean;
  removingPromo: boolean;
  alreadyPurchased: boolean | null;
  errorMessage: string | null;
  processingPayment: boolean;
  isSingleBilledAccount: boolean;
  isPartnerBilledAccount: boolean;
  isEntitled: boolean;
  promoCode: string | null;
  promoDescription: string | null;
  validatingPromoCode: boolean;
  isSuspended: boolean;
  isAccountDisabled: boolean;
  showAddedPromo: boolean;
  promoError: string | null;
  urlParamPromoCode: string | null;
  rating: unknown;
  embedded: unknown;
  campaignActivityId: string | null;
  isStripeAccount: boolean | null;
  stripeQuoteId: string | null;
  stripePaymentToken: string | null;
};

export const initialState: InboxPreviewState = {
  qeId: 'inbox-preview',
  selectedAddDiscount: false,
  successfulPayment: false,
  isTrialer: false,
  removingPromo: false,
  alreadyPurchased: null, // null is used to indicate that the value has not been set yet
  errorMessage: null,
  processingPayment: false,
  isSingleBilledAccount: false,
  isPartnerBilledAccount: false,
  isEntitled: true,
  promoCode: null,
  promoDescription: null,
  validatingPromoCode: false,
  isSuspended: false,
  isAccountDisabled: false,
  showAddedPromo: false,
  promoError: null,
  urlParamPromoCode: null,
  rating: null,
  embedded: null,
  campaignActivityId: null,
  isStripeAccount: null,
  stripeQuoteId: null,
  stripePaymentToken: null,
};

export type InboxPreviewAction =
  | { type: 'setSelectedAddDiscount'; value: boolean }
  | { type: 'onRemovePromoCodeSuccess'; value: Partial<InboxPreviewState> }
  | { type: 'setSuccessfulPayment'; value: boolean }
  | { type: 'setIsTrialer'; value: boolean }
  | { type: 'setIsSuspended'; value: boolean }
  | { type: 'setIsAccountDisabled'; value: boolean }
  | { type: 'setRemovingPromo'; value: boolean }
  | { type: 'setAlreadyPurchased'; value: boolean | null }
  | { type: 'setErrorMessage'; value: string | null }
  | { type: 'setProcessingPayment'; value: boolean }
  | { type: 'setIsSingleBilledAccount'; value: boolean }
  | { type: 'setIsPartnerBilledAccount'; value: boolean }
  | { type: 'setIsEntitled'; value: boolean }
  | { type: 'setPromoCode'; value: string | null }
  | { type: 'setPromoDescription'; value: string | null }
  | { type: 'setPromoError'; value: string | null }
  | { type: 'setValidatingPromoCode'; value: boolean }
  | { type: 'setShowAddedPromo'; value: boolean }
  | { type: 'setUrlParamPromoCode'; value: string | null }
  | { type: 'setRating'; value: unknown }
  | { type: 'setEmbedded'; value: unknown }
  | { type: 'setCampaignActivityId'; value: string | null }
  | { type: 'setIsStripeAccount'; value: boolean }
  | { type: 'setStripeQuoteId'; value: string | null }
  | { type: 'setStripePaymentToken'; value: string | null };

export function inboxPreviewReducer(
  state: InboxPreviewState = initialState,
  action: InboxPreviewAction
): InboxPreviewState {
  switch (action.type) {
    case 'setSelectedAddDiscount': {
      return {
        ...state,
        selectedAddDiscount: action.value,
      };
    }

    case 'onRemovePromoCodeSuccess': {
      return {
        ...state,
        ...action.value,
      };
    }

    case 'setSuccessfulPayment': {
      return {
        ...state,
        successfulPayment: action.value,
      };
    }

    case 'setIsTrialer': {
      return {
        ...state,
        isTrialer: action.value,
      };
    }

    case 'setIsSuspended': {
      return {
        ...state,
        isSuspended: action.value,
      };
    }

    case 'setIsAccountDisabled': {
      return {
        ...state,
        isAccountDisabled: action.value,
      };
    }

    case 'setRemovingPromo': {
      return {
        ...state,
        removingPromo: action.value,
      };
    }

    case 'setAlreadyPurchased': {
      return {
        ...state,
        alreadyPurchased: action.value,
      };
    }

    case 'setErrorMessage': {
      return {
        ...state,
        errorMessage: action.value,
      };
    }

    case 'setProcessingPayment': {
      return {
        ...state,
        processingPayment: action.value,
      };
    }

    case 'setIsSingleBilledAccount': {
      return {
        ...state,
        isSingleBilledAccount: action.value,
      };
    }

    case 'setIsPartnerBilledAccount': {
      return {
        ...state,
        isPartnerBilledAccount: action.value,
      };
    }

    case 'setIsEntitled': {
      return {
        ...state,
        isEntitled: action.value,
      };
    }

    case 'setPromoCode': {
      return {
        ...state,
        promoCode: action.value,
      };
    }

    case 'setPromoDescription': {
      return {
        ...state,
        promoDescription: action.value,
      };
    }

    case 'setPromoError': {
      return {
        ...state,
        promoError: action.value,
      };
    }

    case 'setValidatingPromoCode': {
      return {
        ...state,
        validatingPromoCode: action.value,
      };
    }

    case 'setShowAddedPromo': {
      return {
        ...state,
        showAddedPromo: action.value,
      };
    }

    case 'setUrlParamPromoCode': {
      return {
        ...state,
        urlParamPromoCode: action.value,
      };
    }

    case 'setRating': {
      return {
        ...state,
        rating: action.value,
      };
    }

    case 'setEmbedded': {
      return {
        ...state,
        embedded: action.value,
      };
    }

    case 'setCampaignActivityId': {
      return {
        ...state,
        campaignActivityId: action.value,
      };
    }

    case 'setIsStripeAccount': {
      return {
        ...state,
        isStripeAccount: action.value,
      };
    }

    case 'setStripeQuoteId': {
      return {
        ...state,
        stripeQuoteId: action.value,
      };
    }

    case 'setStripePaymentToken': {
      return {
        ...state,
        stripePaymentToken: action.value,
      };
    }

    default:
      return state;
  }
}

export const InboxPreviewContext = createContext<[InboxPreviewState, (action: InboxPreviewAction) => void]>(
  undefined as never
);

export const useInboxPreviewContext = () => useContext(InboxPreviewContext);
